import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, XCircle } from "lucide-react";
import { api, euro, fmtDateTime } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { metaPurchase } from "@/lib/pixel";

const PaymentSuccess = () => {
  const { lang } = useLang();
  const t = (de, en) => (lang === "de" ? de : en);
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const paypalOrder = params.get("token"); // PayPal hängt ?token=<order_id> an die return_url
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [booking, setBooking] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId && !paypalOrder) { setState("error"); return; }
    let stop = false;

    const pollStripe = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (stop) return;
        if (data.payment_status === "paid") {
          setBooking(data); setState("paid");
          metaPurchase({ value: data.amount, currency: "EUR", bookingNumber: data.booking_number, contentName: data.course_name || "Golfkurs" });
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep polling */ }
      tries.current += 1;
      if (tries.current > 10) { setState("timeout"); return; }
      setTimeout(pollStripe, 2000);
    };

    const capturePaypal = async () => {
      try {
        const { data } = await api.post(`/payments/paypal/capture/${paypalOrder}`);
        if (stop) return;
        if (data.payment_status === "paid") {
          setBooking(data); setState("paid");
          metaPurchase({ value: data.amount, currency: "EUR", bookingNumber: data.booking_number, contentName: data.course_name || "Golfkurs" });
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* retry */ }
      tries.current += 1;
      if (tries.current > 8) { setState("timeout"); return; }
      setTimeout(capturePaypal, 2000);
    };

    if (paypalOrder) capturePaypal(); else pollStripe();
    return () => { stop = true; };
  }, [sessionId, paypalOrder]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 pt-28 pb-32" data-testid="payment-success-page">
      <div className="max-w-lg w-full text-center">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 size={44} className="text-[#ff0033] animate-spin mx-auto" />
            <p className="text-neutral-300 mt-6 font-mono-lab uppercase tracking-widest text-sm">{t("Zahlung wird bestätigt…", "Confirming payment…")}</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-confirmed">
            <div className="w-16 h-16 rounded-full bg-[#ff0033] flex items-center justify-center mx-auto mb-5"><Check size={32} className="text-white" /></div>
            <h1 className="font-heading text-4xl md:text-5xl uppercase text-white tracking-tight">{t("Buchung bestätigt!", "Booking confirmed!")}</h1>
            <p className="text-neutral-400 mt-4">{t("Buchungsnummer", "Booking number")}: <span className="text-[#ff0033] font-mono-lab">{booking?.booking_number}</span></p>
            {booking?.course_name && <p className="text-neutral-300 mt-2">{booking.course_name} · {fmtDateTime(booking.start, lang)}</p>}
            {booking?.amount != null && <p className="text-white font-heading text-2xl mt-3">{euro(booking.amount)}</p>}
            <p className="text-neutral-500 text-sm mt-4">{t("Eine Bestätigung wurde an", "A confirmation was sent to")} {booking?.email} {t("gesendet.", "sent.")}</p>
            <Link to="/" className="inline-block mt-8 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-8 py-3 hover:bg-[#cc0029] transition-colors" data-testid="success-home-btn">{t("Zur Startseite", "Back home")}</Link>
          </div>
        )}
        {(state === "timeout" || state === "error") && (
          <div data-testid="payment-pending">
            <XCircle size={44} className="text-neutral-500 mx-auto" />
            <h1 className="font-heading text-3xl uppercase text-white tracking-tight mt-5">{t("Zahlung wird verarbeitet", "Payment processing")}</h1>
            <p className="text-neutral-400 mt-4">{t("Deine Zahlung wird noch verarbeitet. Du erhältst die Bestätigung per E-Mail, sobald sie abgeschlossen ist.", "Your payment is still being processed. You'll receive a confirmation by email once it's complete.")}</p>
            <Link to="/" className="inline-block mt-8 border border-white/20 text-white font-heading uppercase tracking-wider px-8 py-3 hover:border-[#ff0033] hover:text-[#ff0033] transition-colors">{t("Zur Startseite", "Back home")}</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
